/**
 * SessionMetrics - Smart event aggregation to reduce noise
 *
 * Instead of firing events on every interaction, this service:
 * 1. Aggregates counts during a session
 * 2. Fires summary events on session end
 * 3. Tracks milestones (5, 10, 25, 50, 100) for progress
 * 4. Tracks first-time events only once per user
 * 5. Samples high-frequency events (only 10% of users)
 *
 * @example
 * // High-frequency action - aggregated
 * SessionMetrics.increment('shapes_created');
 *
 * // Milestone tracking - fires at thresholds
 * SessionMetrics.milestone('shapes_created', count);
 *
 * // First-time only - fires once ever
 * SessionMetrics.firstTime('export.pdf');
 *
 * // Sampled - only 10% of users
 * SessionMetrics.sampled('zoom.used');
 */

import { Tracker } from './Tracker';

// Milestones that trigger events
const MILESTONES = [5, 10, 25, 50, 100, 250, 500] as const;

// Sampling rate for high-frequency events (10%)
const SAMPLE_RATE = 0.1;

// Storage keys
const FIRST_TIME_KEY = 'creately_first_time_events';
const SAMPLE_COHORT_KEY = 'creately_sample_cohort';

type MetricCounts = Record<string, number>;
type MilestoneTracked = Record<string, Set<number>>;

type BrowserWindowLike = {
  location: { pathname: string };
  addEventListener?: (event: string, listener: () => void) => void;
};

type BrowserDocumentLike = {
  referrer?: string;
  visibilityState?: string;
  addEventListener?: (event: string, listener: () => void) => void;
};

type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

class SessionMetricsService {
  private counts: MetricCounts = {};
  private milestones: MilestoneTracked = {};
  private sessionStart: number = Date.now();
  private isInSampleCohort: boolean;
  private initialized = false;

  constructor() {
    // Determine if this user is in the 10% sample cohort (stable per user)
    this.isInSampleCohort = this.getOrCreateSampleCohort();
  }

  /**
   * Initialize session tracking - call once on app mount
   */
  init(): void {
    const browserWindow = this.getBrowserWindow();
    const browserDocument = this.getDocument();
    if (this.initialized || !browserWindow) return;
    this.initialized = true;
    this.sessionStart = Date.now();

    // Track session start
    Tracker.track('session.start', {
      url: browserWindow.location.pathname,
      referrer: browserDocument?.referrer || undefined,
    });

    // Fire session summary on page unload
    browserWindow.addEventListener?.('beforeunload', this.flushSession);

    // Also fire on visibility change (tab switch/minimize)
    browserDocument?.addEventListener?.('visibilitychange', () => {
      if (browserDocument.visibilityState === 'hidden') {
        this.flushSession();
      }
    });
  }

  /**
   * Increment a counter - aggregated, fires on session end
   * Use for high-frequency actions like shape creation, selection, etc.
   */
  increment(metric: string, delta = 1): void {
    this.counts[metric] = (this.counts[metric] || 0) + delta;
  }

  /**
   * Track milestone achievements - fires immediately when threshold crossed
   * Use for progress tracking (shapes created, fields filled, etc.)
   */
  milestone(metric: string, currentValue: number): void {
    if (!this.milestones[metric]) {
      this.milestones[metric] = new Set();
    }

    for (const threshold of MILESTONES) {
      if (currentValue >= threshold && !this.milestones[metric].has(threshold)) {
        this.milestones[metric].add(threshold);
        Tracker.track(`milestone.${metric}`, {
          threshold,
          actual: currentValue,
        });
      }
    }
  }

  /**
   * Track first-time events - fires only once ever per user
   * Use for feature discovery (first export, first share, first AI use, etc.)
   */
  firstTime(eventName: string, properties?: Record<string, unknown>): void {
    const tracked = this.getFirstTimeEvents();
    if (tracked.has(eventName)) return;

    tracked.add(eventName);
    this.saveFirstTimeEvents(tracked);

    Tracker.track(`first.${eventName}`, properties);
  }

  /**
   * Sampled tracking - only fires for 10% of users
   * Use for ultra-high-frequency events like zoom, pan, cursor movement
   */
  sampled(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isInSampleCohort) return;
    Tracker.track(`sampled.${eventName}`, {
      ...properties,
      _sampled: true,
      _sample_rate: SAMPLE_RATE,
    });
  }

  /**
   * Track duration - use for timing important flows
   */
  startTimer(timerName: string): void {
    this.counts[`_timer_${timerName}`] = Date.now();
  }

  endTimer(timerName: string, eventName: string): void {
    const startKey = `_timer_${timerName}`;
    const startTime = this.counts[startKey];
    if (!startTime) return;

    const duration = Date.now() - startTime;
    delete this.counts[startKey];

    // Only track if duration is reasonable (< 30 min)
    if (duration < 30 * 60 * 1000) {
      Tracker.track(eventName, { duration_ms: duration });
    }
  }

  /**
   * Flush session metrics - called on page unload
   */
  private flushSession = (): void => {
    const sessionDuration = Date.now() - this.sessionStart;

    // Filter out timer keys and empty counts
    const metricCounts = Object.entries(this.counts)
      .filter(([key, value]) => !key.startsWith('_timer_') && value > 0)
      .reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {} as Record<string, number>
      );

    // Only fire if there's meaningful activity
    if (Object.keys(metricCounts).length === 0) return;

    // Send aggregated session summary
    Tracker.track('session.summary', {
      duration_ms: sessionDuration,
      ...metricCounts,
    });

    // Reset for potential new session (SPA navigation)
    this.counts = {};
    this.sessionStart = Date.now();
  };

  // --- Storage helpers ---

  private getFirstTimeEvents(): Set<string> {
    const storage = this.getStorage();
    if (!storage) return new Set();
    try {
      const stored = storage.getItem(FIRST_TIME_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  }

  private saveFirstTimeEvents(events: Set<string>): void {
    const storage = this.getStorage();
    if (!storage) return;
    try {
      storage.setItem(FIRST_TIME_KEY, JSON.stringify([...events]));
    } catch {
      // Storage full or unavailable
    }
  }

  private getOrCreateSampleCohort(): boolean {
    const storage = this.getStorage();
    if (!storage) return false;
    try {
      const stored = storage.getItem(SAMPLE_COHORT_KEY);
      if (stored !== null) {
        return stored === 'true';
      }

      // Randomly assign to sample cohort
      const inCohort = Math.random() < SAMPLE_RATE;
      storage.setItem(SAMPLE_COHORT_KEY, String(inCohort));
      return inCohort;
    } catch {
      // Default to not sampling if storage unavailable
      return false;
    }
  }

  private getBrowserWindow(): BrowserWindowLike | null {
    const candidate = (globalThis as { window?: unknown }).window;
    if (!candidate || typeof candidate !== 'object') return null;
    const maybe = candidate as Partial<BrowserWindowLike>;
    if (!maybe.location || typeof maybe.location.pathname !== 'string') return null;
    return maybe as BrowserWindowLike;
  }

  private getDocument(): BrowserDocumentLike | null {
    const candidate = (globalThis as { document?: unknown }).document;
    if (!candidate || typeof candidate !== 'object') return null;
    return candidate as BrowserDocumentLike;
  }

  private getStorage(): StorageLike | null {
    const candidate = (globalThis as { localStorage?: unknown }).localStorage;
    if (!candidate || typeof candidate !== 'object') return null;
    const maybe = candidate as Partial<StorageLike>;
    if (typeof maybe.getItem !== 'function' || typeof maybe.setItem !== 'function') return null;
    return maybe as StorageLike;
  }
}

// Singleton instance
export const SessionMetrics = new SessionMetricsService();
